<template>
  <r-ai-page-container :current-step="6">
    <v-row class="text-start">
      <v-col cols="12">
        <div class="pb-4 text-body-1" v-if="!addMode">
          {{ textIntro }}
        </div>
        <r-ai-bubble v-if="!addMode && !isEditing">
          <ai-line title="Company" :text="workGeneralDisplay"></ai-line>
          <ai-line title="Position" :text="workRoleDisplay"></ai-line>
          <ai-line title="Dates Worked" :text="workDateDisplay"></ai-line>
          <ai-line title="Location" :text="workLocationDisplay"></ai-line>
        </r-ai-bubble>
        <r-ai-bubble v-if="isEditing" class="text-body-1 mt-2 mb-5">
          <div class="py-2 mb-2">Work Details</div>
          <v-text-field
            v-model="companyName"
            label="Company or Organization name"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="companyCity"
            label="City"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="companyState"
            label="State"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="companyCountry"
            label="Country"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="position"
            label="Work Position"
            outlined
            dense
          ></v-text-field>
          <v-switch
            class="mt-0"
            v-model="workingCurrently"
            label="Are you currently working here?"
          ></v-switch>
          <ai-month-date-select
            class="mb-2"
            label="Start Date"
            :value.sync="startDate"
          ></ai-month-date-select>
          <ai-month-date-select
            v-if="!workingCurrently"
            label="End Date"
            :value.sync="endDate"
          ></ai-month-date-select>
        </r-ai-bubble>
        <div v-if="isEditing">
          <v-btn
            class="mt-2 mb-2"
            color="primary"
            outlined
            @click="save()"
            block
          >
            {{ addMode ? "Save Job" : "Save and Continue" }}
          </v-btn>
        </div>
        <div class="pt-2" v-if="!addMode && !isEditing">
          <v-btn class="my-4" color="primary" @click="next()" block>
            Looks Good
          </v-btn>
          <v-btn class="my-4 ai-outlined-btn" outlined @click="edit()" block>
            Edit or Add Info
          </v-btn>
          <v-btn
            class="my-4 ai-outlined-btn"
            outlined
            @click="saveAndExitToDone"
            block
          >
            Save and Exit Agent
          </v-btn>
          <!-- PR-AGENT-ESCAPE: link to the main work-history editor for
               users who'd rather use the regular UI. -->
          <div class="text-center mt-3">
            <a
              href="#"
              class="text-caption text--secondary"
              @click.prevent="$router.push('/Work')"
            >
              Switch to manual entry
            </a>
          </div>
        </div>
        <!-- PR-AGENT-UX: end-of-section "add more" screen. After the user
             reviews every parsed job they land here — a clear list of
             every record on file plus a dedicated button to add jobs the
             parser missed. Keeps "review" and "add" as separate screens. -->
        <div v-if="addMode && !isEditing">
          <div class="pb-4 text-body-1">{{ addListIntro }}</div>
          <r-ai-bubble class="text-body-1 mt-2 mb-4">
            <div class="py-2 mb-1 font-weight-medium">
              Your Work History ({{ workCount }})
            </div>
            <div
              v-for="item in fullList"
              :key="item.key"
              class="ai-add-list-row py-2"
            >
              <div class="font-weight-medium">
                {{ rowTitle(item.general, "New job — tap to fill in") }}
              </div>
              <div class="text-body-2 text--secondary">
                {{ rowTitle(rolesText(item), "Position not yet entered") }}
              </div>
              <!-- PR-WORK-HISTORY-DATES: surface the date range on each
                   work entry so the user can spot a timeline at a glance.
                   item.date is pre-formatted by buildDateDisplay (e.g.,
                   "Jan 2020 — Present"). Hidden when blank so unfilled
                   new-job placeholders don't show a dangling "Dates: —". -->
              <div
                v-if="item.date && item.date.trim()"
                class="text-caption text--secondary mt-1 d-flex align-center"
              >
                <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
                {{ item.date }}
              </div>
            </div>
            <div
              v-if="workCount === 0"
              class="text-body-2 text--secondary py-2"
            >
              No work records yet.
            </div>
          </r-ai-bubble>
          <!-- PR-BUTTON-ORDER: Continue moved ABOVE Add so rapid-clickers
               from the previous review pages don't muscle-memory-click into
               a blank new entry. Add is still one-click reachable as the
               secondary outlined button below. -->
          <v-btn
            class="my-4"
            color="primary"
            @click="continueToAchievements"
            block
          >
            Continue to Accomplishments
          </v-btn>
          <v-btn
            class="my-2 ai-outlined-btn"
            outlined
            @click="addAnotherJob"
            block
          >
            <v-icon left>mdi-plus</v-icon>
            Add a Job
          </v-btn>
          <v-btn
            class="my-2 ai-outlined-btn"
            outlined
            @click="saveAndExitToDone"
            block
          >
            Save and Exit Agent
          </v-btn>
          <!-- PR-AGENT-ESCAPE: link to the main work-history editor for
               users who'd rather use the regular UI. -->
          <div class="text-center mt-3">
            <a
              href="#"
              class="text-caption text--secondary"
              @click.prevent="$router.push('/Work')"
            >
              Switch to manual entry
            </a>
          </div>
        </div>
      </v-col>
    </v-row>
  </r-ai-page-container>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapGetters, mapActions, mapMutations } from "vuex";
import * as builder from "../../store/modules/work_builder";
import * as UUID from "../../utility/guid";
import AIMonthDateSelect from "../../components/AI/AIMonthDateSelect.vue";
import AILine from "../../components/AI/AILine.vue";

export default {
  name: "AIWork",
  data: () => ({
    isEditing: false,
    addMode: false,
    keyIndex: 0,
    noValues: false
  }),
  components: {
    "ai-month-date-select": AIMonthDateSelect,
    "ai-line": AILine
  },
  methods: {
    // PR-AGENT-UX: same pattern as AIEducation.addAnotherDegree.
    addAnotherJob() {
      var newKey = UUID.gen();
      this.createEntry({ key: newKey });
      this.key = newKey;
      this.keyIndex = (this.fullList || []).findIndex(e => e.key === newKey);
      if (this.keyIndex < 0) this.keyIndex = (this.fullList || []).length - 1;
      this.addMode = true;
      this.isEditing = true;
    },
    // PR-AGENT-UX: save before navigating to AIDone.
    async saveAndExitToDone() {
      try {
        await this.saveWork();
      } catch (e) {
        console.warn("saveAndExitToDone failed:", e);
      }
      this.$router.push({ path: "/AIDone" });
    },
    next() {
      if (this.keyIndex + 1 < this.fullList.length) {
        this.keyIndex += 1;
        this.key = this.fullList[this.keyIndex].key;
        this.isEditing = false;
      } else {
        // PR-AGENT-UX: reviewed every parsed job — land on the "add more"
        // screen instead of jumping straight to accomplishments.
        this.addMode = true;
        this.isEditing = false;
      }
    },
    continueToAchievements() {
      this.$router.push({ path: "/AIWorkAchievements" });
    },
    rowTitle(value, fallback) {
      return value && String(value).trim() ? value : fallback;
    },
    rolesText(item) {
      return ((item && item.roles) || []).join(", ");
    },
    edit() {
      this.isEditing = true;
    },
    async save() {
      await this.saveWork();
      if (this.addMode) {
        // Saved a freshly-added job — return to the add-more list.
        this.isEditing = false;
      } else {
        this.next();
      }
    },
    ...mapMutations("work", ["createEntry"]),
    ...mapActions("work", ["saveWork"])
  },
  computed: {
    ...mapGetters("work", ["fullList", "doesKeyExist", "baseDegrees"]),
    ...mapFields("work", {
      key: "key",
      display: "workGeneral.display",
      companyName: "workGeneral.companyName",
      companyCity: "workGeneral.companyCity",
      companyState: "workGeneral.companyState",
      companyCountry: "workGeneral.companyCountry",
      industry: "workGeneral.industry",
      workDict: "workDict",
      startDate: "workGeneral.startDate",
      endDate: "workGeneral.endDate",
      positions: "workRole.positions",
      description: "workResponsibilities.description",
      workingCurrently: "workGeneral.workingCurrently",
      workGeneral: "workGeneral",
      workRole: "workRole"
    }),
    position: {
      get() {
        if (this.positions && this.positions.length > 0) {
          return this.positions[0].title;
        }
        return "";
      },
      set(newvalue) {
        if (this.positions && this.positions.length > 0) {
          this.positions[0].title = newvalue;
        } else {
          this.positions = [{ title: newvalue, role: "", key: "1" }];
        }
      }
    },
    workCount() {
      return (this.fullList || []).length;
    },
    addListIntro() {
      return this.workCount > 0
        ? "That's everything I have for your work history. Add any jobs I missed, or continue to your accomplishments."
        : "Add your work history here, or continue to your accomplishments.";
    },
    textIntro() {
      if (this.noValues) {
        return "We didn't find any work entries in your resume. You can add one in the full resumation tool.";
      } else if (this.keyIndex === 0) {
        return "Here's what I found for your first Work Record:";
      } else {
        return "Here's what I found for your next Work Record:";
      }
    },
    workRoleDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildRolesDisplay(this.workRole).join(", ");
    },
    workDateDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildDateDisplay(this.workGeneral);
    },
    workLocationDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildLocationDisplay(this.workGeneral);
    },
    workGeneralDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildGeneralDisplay(this.workGeneral);
    }
    // educationDisplayLine2() {
    //   if (this.noValues) {
    //     return "";
    //   }
    //   return `${builder.buildDegree(
    //     this.educationDegree
    //   )} ${builder.buildMinorDegree(this.educationDegreeMinor)}`.trim();
    // },
    // educationDisplayLine3() {
    //   if (this.noValues) {
    //     return "";
    //   }
    //   return builder.buildLocationDisplay(this.educationGeneral);
    // },
    // educationDisplayLine4() {
    //   if (this.noValues) {
    //     return "";
    //   }
    //   return builder.buildGPADisplay(this.educationGPA);
    // },
  },
  beforeMount() {
    console.log("AIWork beforeMount");
    if (this.fullList && this.fullList.length > 0) {
      // load first record
      this.key = this.fullList[0].key;
    } else {
      this.noValues = true;
    }
  },
  mounted() {
    // console.log("AIWork mounted");
    // // check if any work records exist. If so, load the first key.
    // if (this.fullList && this.fullList.length > 0) {
    //   // load first record
    //   this.key = this.fullList[0].key;
    // } else {
    //   this.noValues = true;
    // }
  }
};
</script>

<style scoped>
.edit-bubble {
  background-color: #f9fafb;
  border-radius: 10px;
  padding: 16px;
}

.add-bubble {
  background-color: #eff6ff;
  border-radius: 10px;
  padding: 16px;
}

.ai-add-list-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.ai-add-list-row:last-child {
  border-bottom: none;
}
.theme--dark .ai-add-list-row {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}
</style>
